import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Bot, ShieldCheck } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';

// Machine agents this human has authorized to act ON THEIR BEHALF through the MCP
// delegation surface. The proxy scopes every read/write to the caller's own uid
// (agent_delegations/{my_uid}) — you can only ever manage your own delegations.
export default function AgentDelegations() {
  const [agents, setAgents] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAgent, setNewAgent] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const { t } = useTranslation(['admin', 'common']);
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);

  const load = () => {
    apiFetch('/api/delegations')
      .then((r) => r.json())
      .then((data) => setAgents(Array.isArray(data.agents) ? data.agents : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const flash = (text: string) => {
    setMsg(text);
    window.setTimeout(() => setMsg(''), 4000);
  };

  const add = async () => {
    const agent = newAgent.trim();
    if (!agent) return;
    setBusy(true);
    try {
      const resp = await apiFetch('/api/delegations', {
        method: 'POST',
        body: JSON.stringify({ agent }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setAgents(Array.isArray(data.agents) ? data.agents : []);
        setNewAgent('');
        flash(t('delegations.authorized', { agent }));
      } else {
        flash(t('delegations.failedWith', { reason: data.error || resp.status }));
      }
    } catch {
      flash(t('delegations.failed'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (agent: string) => {
    setBusy(true);
    try {
      const resp = await apiFetch(`/api/delegations/${encodeURIComponent(agent)}`, {
        method: 'DELETE',
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setAgents(Array.isArray(data.agents) ? data.agents : []);
        flash(t('delegations.revoked', { agent }));
      } else {
        flash(t('delegations.failedWith', { reason: data.error || resp.status }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-8 py-7">
      <>
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-5 w-5 text-primary" />
          <h2 className="m-0 font-display text-[22px] font-semibold">{t('delegations.heading')}</h2>
          <HelpDot>{t('delegations.help')}</HelpDot>
        </div>
        <p className="mb-4 mt-2 text-sm leading-relaxed text-muted-foreground">{t('delegations.body1')}</p>
        <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{t('delegations.body2')}</p>

        <Card className="mb-6">
          <CardContent className="py-4">
            <label className="text-xs font-medium text-muted-foreground">{t('delegations.addPlaceholder')}</label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm bg-background font-mono"
                placeholder={t('delegations.placeholder')}
                value={newAgent}
                onChange={(e) => setNewAgent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') add();
                }}
              />
              <Button size="sm" disabled={busy || !newAgent.trim()} onClick={add}>
                <Plus className="mr-1 h-4 w-4" /> {t('delegations.add')}
              </Button>
            </div>
            {msg && <p className="mt-2 text-xs text-muted-foreground">{msg}</p>}
          </CardContent>
        </Card>

        {loading && <p className="text-muted-foreground">{t('state.loading', { ns: 'common' })}</p>}
        {!loading && agents.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">{t('delegations.empty')}</CardContent>
          </Card>
        )}
        {!loading && agents.length > 0 && (
          <div className="space-y-2">
            {agents.map((a) => (
              <Card key={a}>
                <CardContent className="py-3 flex items-center gap-3">
                  <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="font-mono text-sm flex-1 truncate">{a}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    disabled={busy}
                    onClick={() => setPendingRevoke(a)}
                  >
                    <Trash2 className="mr-1 h-4 w-4" /> {t('delegations.revoke')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <ConfirmDialog
          open={!!pendingRevoke}
          onOpenChange={(o) => !o && setPendingRevoke(null)}
          title={t('delegations.revokeTitle')}
          warning={t('delegations.revokeWarning')}
          destructive
          confirmLabel={t('delegations.revokeConfirm')}
          onConfirm={() => pendingRevoke && revoke(pendingRevoke)}
        />
      </>
    </div>
  );
}
